import React, {useState} from 'react';

import {hooks} from '../hooks';
import {svg} from '../assets/svg';
import {salon} from '../config/salon';
import {PwaInstallButton} from './PwaInstallButton';

type Props = {
  title?: string;
  search?: boolean;
  showLogo?: boolean;
  showGoBack?: boolean;
  showBasket?: boolean;
  headerStyle?: React.CSSProperties;
};

export const Header: React.FC<Props> = ({
  title,
  search,
  showLogo,
  showGoBack,
  showBasket,
  headerStyle,
}) => {
  const navigate = hooks.useNavigate();
  const location = hooks.useLocation();

  const [showModal, setShowModal] = useState(false);
  const [themeColor, setThemeColor] = useState('#fff');
  const [dragStartX, setDragStartX] = useState<number | null>(null);

  hooks.useThemeColor(themeColor);

  const openContactMenu = () => {
    setThemeColor('#F5FAFB');
    setShowModal(true);
  };

  const renderLogo = (): JSX.Element | null => {
    if (!showLogo) return null;

    return (
      <button
        style={{
          height: '100%',
          width: 'auto',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
        }}
        className='clickable'
        onClick={openContactMenu}
      >
        <span className='brand-lockup'>
          <strong>{salon.brand}</strong>
          <span className='signature-text'>{salon.tagline}</span>
        </span>
      </button>
    );
  };

  const renderPullHandle = (): JSX.Element | null => {
    if (!showLogo || showModal) return null;

    return (
      <button
        className='menu-pull-handle'
        onClick={openContactMenu}
        onPointerDown={(event) => setDragStartX(event.clientX)}
        onPointerMove={(event) => {
          if (dragStartX !== null && event.clientX - dragStartX > 28) {
            openContactMenu();
            setDragStartX(null);
          }
        }}
        onPointerUp={(event) => {
          if (dragStartX !== null && event.clientX - dragStartX > 28) {
            openContactMenu();
          }

          setDragStartX(null);
        }}
        onPointerCancel={() => setDragStartX(null)}
        onMouseDown={(event) => setDragStartX(event.clientX)}
        onMouseMove={(event) => {
          if (dragStartX !== null && event.clientX - dragStartX > 28) {
            openContactMenu();
            setDragStartX(null);
          }
        }}
        onMouseUp={(event) => {
          if (dragStartX !== null && event.clientX - dragStartX > 28) {
            openContactMenu();
          }

          setDragStartX(null);
        }}
        onTouchStart={(event) => setDragStartX(event.touches[0]?.clientX ?? null)}
        onTouchMove={(event) => {
          const currentX = event.touches[0]?.clientX;

          if (
            dragStartX !== null &&
            currentX !== undefined &&
            currentX - dragStartX > 28
          ) {
            openContactMenu();
            setDragStartX(null);
          }
        }}
        type='button'
        aria-label='Menüyü açmak için sağa çekin'
      >
        <span className='menu-pull-handle__chevron'>›</span>
      </button>
    );
  };

  const renderGoBack = (): JSX.Element | null => {
    if (showGoBack && location.key !== 'default')
      return (
        <div
          onClick={() => navigate(-1)}
          style={{
            position: 'absolute',
            left: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
          }}
          className='clickable'
        >
          <svg.GoBackSvg />
        </div>
      );

    return null;
  };

  const renderTitle = (): JSX.Element | null => {
    return (
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <h4 style={{marginBottom: 2}}>{title}</h4>
      </div>
    );
  };

  const renderSearch = (): JSX.Element | null => {
    if (!search) return null;

    return (
      <div
        style={{
          position: 'absolute',
          left: '50%',
          height: '100%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <svg.SearchHeaderSvg />
      </div>
    );
  };

  const renderBasket = (): JSX.Element | null => {
    if (!showBasket) return null;

    return (
      <div className='header-actions'>
        <PwaInstallButton className='header-install-button' />
        <a
          className='header-call-button'
          href={salon.phoneHref}
          aria-label='Hızlı ara'
        >
          Ara
        </a>
      </div>
    );
  };

  const renderModal = (): JSX.Element | null => {
    if (!showModal) return null;
    return (
      <>
        <div
          className='contact-drawer-backdrop'
          onClick={() => {
            setThemeColor('#F5FAFB');
            setShowModal(false);
          }}
        />
        <div
          className='contact-drawer'
        >
          <div className='contact-drawer__title'>
            <span className='contact-drawer__eyebrow signature-text'>
              {salon.tagline}
            </span>
            <h2>{salon.brand}</h2>
          </div>
          <div className='contact-row'>
            <span className='contact-row__label'>Adres</span>
            <a
              className='contact-row__value'
              href={salon.mapsHref}
              target='_blank'
              rel='noreferrer'
            >
              {salon.address}
            </a>
          </div>
          <div className='contact-row'>
            <span className='contact-row__label'>Telefon</span>
            <a
              className='contact-row__value'
              href={salon.phoneHref}
            >
              {salon.phone}
            </a>
          </div>
          <div className='contact-row'>
            <span className='contact-row__label'>Instagram</span>
            <a
              className='contact-row__value'
              href={salon.instagramHref}
              target='_blank'
              rel='noreferrer'
            >
              {salon.instagramHandle}
            </a>
          </div>
          <div className='contact-drawer__actions'>
            <PwaInstallButton className='salon-button salon-button--light' />
            <a
              className='salon-button salon-button--dark'
              href={salon.phoneHref}
            >
              Ara
            </a>
            <a
              className='salon-button salon-button--outline'
              href={salon.whatsappHref}
              target='_blank'
              rel='noreferrer'
            >
              WhatsApp
            </a>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <header
        style={{
          position: 'relative',
          height: 'var(--header-height)',
          backgroundColor: 'var(--main-background)',
          ...headerStyle,
        }}
        className='row-center-space-between'
      >
        {renderLogo()}
        {renderGoBack()}
        {renderTitle()}
        {renderSearch()}
        {renderBasket()}
      </header>
      {renderPullHandle()}
      {renderModal()}
    </>
  );
};
